/**
 * OpsApprovalsPage — Executive-Director-only queue of projects awaiting
 * delivery approval. Route: /ops/approvals
 *
 * The Director can approve (project → delivered) or reject (project → active)
 * with an optional note. Contract value can be recorded on approval so KPIs
 * capture the final billed amount.
 */
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { opsApi, formatAuthError } from './OpsAuthContext';
import OpsLayout from './OpsLayout';
import { fmtDate } from './opsConstants';

const OpsApprovalsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState('');
  const [selected, setSelected] = useState(null);
  const [decisionNote, setDecisionNote] = useState('');
  const [contractValue, setContractValue] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await opsApi.get('/approvals');
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const openDecision = (p) => {
    setSelected(p);
    setDecisionNote('');
    setContractValue(p.contract_value ? String(p.contract_value) : '');
    setErr('');
  };

  const submitApprove = async () => {
    if (!selected) return;
    setBusy(true);
    setErr('');
    try {
      const payload = { note: decisionNote };
      if (contractValue) payload.contract_value = parseFloat(contractValue);
      await opsApi.post(`/projects/${selected.id}/approve`, payload);
      setBanner(`تم اعتماد التسليم ✅ · ${selected.code}`);
      setSelected(null);
      refresh();
      setTimeout(() => setBanner(''), 3500);
    } catch (e) {
      setErr(formatAuthError(e));
    } finally {
      setBusy(false);
    }
  };

  const submitReject = async () => {
    if (!selected) return;
    if (!decisionNote.trim()) {
      setErr('يرجى ذكر سبب الرفض / التعديلات المطلوبة');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await opsApi.post(`/projects/${selected.id}/reject-approval`, { note: decisionNote });
      setBanner('تمت إعادة المشروع لقائد المسار مع الملاحظات');
      setSelected(null);
      refresh();
      setTimeout(() => setBanner(''), 3500);
    } catch (e) {
      setErr(formatAuthError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <OpsLayout title="طلبات اعتماد التسليم" subtitle="Delivery Approvals · Close-the-loop">
      {banner && (
        <div className="mb-4 bg-[#E3EDDE] border border-[#B8CBA2] text-[#2C3814] rounded-xl px-4 py-2.5 text-[13px] font-['Noto_Kufi_Arabic']">
          {banner}
        </div>
      )}

      {loading && (
        <p className="text-[13px] text-[#7A866E] font-['Noto_Kufi_Arabic']">جارٍ التحميل…</p>
      )}

      {!loading && items.length === 0 && (
        <div
          data-testid="approvals-empty"
          className="bg-white rounded-2xl ring-1 ring-[#E6E0D6] p-10 text-center"
        >
          <p className="font-['Noto_Kufi_Arabic'] text-[14px] text-[#5C6D49]">
            لا توجد طلبات اعتماد معلَّقة حاليًا 🌿
          </p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="bg-white rounded-2xl ring-1 ring-[#E6E0D6] overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-[#F3F1E9] text-[#3D512D] font-['Noto_Kufi_Arabic']">
              <tr>
                <th className="text-start px-4 py-3">المشروع</th>
                <th className="text-start px-4 py-3">العميل</th>
                <th className="text-start px-4 py-3">قائد المسار</th>
                <th className="text-start px-4 py-3">طُلب في</th>
                <th className="text-start px-4 py-3">القيمة</th>
                <th className="text-start px-4 py-3">إجراء</th>
              </tr>
            </thead>
            <tbody className="font-['Noto_Kufi_Arabic']">
              {items.map((p) => (
                <tr
                  key={p.id}
                  className="border-t border-[#EFEAE1] hover:bg-[#FAFAF7]"
                  data-testid={`approval-row-${p.code}`}
                >
                  <td className="px-4 py-3">
                    <Link to={`/ops/projects/${p.id}`} className="text-[#1F3D2B] font-semibold hover:underline">
                      {p.code}
                    </Link>
                    <p className="text-[11px] text-[#7A866E] mt-1">{p.service_type}</p>
                  </td>
                  <td className="px-4 py-3 text-[#1F3D2B]">{p.client_name || '—'}</td>
                  <td className="px-4 py-3 text-[#5C6D49]" dir="ltr">{p.lead_email}</td>
                  <td className="px-4 py-3 text-[#5C6D49]">{fmtDate(p.approval_requested_at)}</td>
                  <td className="px-4 py-3 text-[#4A5D23]">
                    {p.contract_value ? `${p.contract_value.toLocaleString('ar-SA')} ر.س` : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`decide-${p.code}`}
                      onClick={() => openDecision(p)}
                      className="text-[11px] font-semibold bg-[#1F3D2B] text-white px-3 py-1.5 rounded-lg hover:bg-[#163020]"
                    >
                      قرار
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          dir="rtl"
        >
          <div
            data-testid="approval-decision-modal"
            className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
          >
            <h3 className="font-['Noto_Kufi_Arabic'] text-[18px] font-bold text-[#1F3D2B]">
              قرار اعتماد التسليم
            </h3>
            <p className="text-[12.5px] font-['Noto_Kufi_Arabic'] text-[#5C6D49] mt-1">
              {selected.code} · {selected.client_name}
            </p>
            {selected.approval_note && (
              <div className="mt-3 bg-[#FAFAF7] rounded-lg p-3 text-[12px] font-['Noto_Kufi_Arabic'] text-[#3D512D]">
                <span className="text-[#7A866E]">ملاحظة القائد: </span>
                {selected.approval_note}
              </div>
            )}
            <label className="block text-[11px] font-['Noto_Kufi_Arabic'] text-[#5C6D49] mt-4 mb-1">
              القيمة النهائية للعقد (ر.س) — اختياري
            </label>
            <input
              data-testid="approval-value"
              type="number"
              value={contractValue}
              onChange={(e) => setContractValue(e.target.value)}
              className="w-full text-[13px] font-['Noto_Kufi_Arabic'] border border-[#D8D2C8] rounded-lg px-3 py-2 bg-white"
              placeholder="0"
              dir="ltr"
            />
            <label className="block text-[11px] font-['Noto_Kufi_Arabic'] text-[#5C6D49] mt-3 mb-1">
              ملاحظة (تُرسل لقائد المسار)
            </label>
            <textarea
              data-testid="approval-note"
              value={decisionNote}
              onChange={(e) => setDecisionNote(e.target.value)}
              className="w-full text-[13px] font-['Noto_Kufi_Arabic'] border border-[#D8D2C8] rounded-lg px-3 py-2 bg-white min-h-[90px]"
              placeholder="ملاحظة للاعتماد أو سبب الرفض…"
            />
            {err && (
              <p className="text-[12px] text-[#B84A3A] font-['Noto_Kufi_Arabic'] mt-2">{err}</p>
            )}
            <div className="flex gap-2 mt-5">
              <button
                data-testid="approval-approve"
                disabled={busy}
                onClick={submitApprove}
                className="text-[13px] font-['Noto_Kufi_Arabic'] font-semibold bg-[#1F3D2B] text-white px-5 py-2 rounded-lg hover:bg-[#163020] disabled:opacity-50"
              >
                اعتماد التسليم
              </button>
              <button
                data-testid="approval-reject"
                disabled={busy}
                onClick={submitReject}
                className="text-[13px] font-['Noto_Kufi_Arabic'] font-semibold border border-[#B84A3A] text-[#B84A3A] px-5 py-2 rounded-lg hover:bg-[#FBEDEA] disabled:opacity-50"
              >
                رفض وإعادة
              </button>
              <button
                onClick={() => setSelected(null)}
                className="text-[13px] font-['Noto_Kufi_Arabic'] text-[#3D512D] px-5 py-2 rounded-lg hover:bg-[#F3F1E9] ms-auto"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </OpsLayout>
  );
};

export default OpsApprovalsPage;
